import { Observable, Subject, combineLatest } from "rxjs";
import { map, startWith } from "rxjs/operators";
import { createLocalStorage } from "./createLocalStorage";
import { atomWriteAttribute, matchMedia$ } from "./dom";

export const COLOR_SCHEME = ["dark", "light"] as const;
export type ColorScheme = (typeof COLOR_SCHEME)[number];

// import "@zyzgroup/core-web/dist/css/theme.css";
export type Theme =
  | "xg-blue" // 信鸽蓝
  | "xg-red" // 信鸽红
  | "xg-green" // 信鸽绿
  | "ks-green" // 昆山绿
  | "yz-green"; // 扬州绿

export type Colors = ReturnType<typeof getThemeColors>;

const { get: getColorSchemeInLocalStorage, set: setColorSchemeInLocalStorage } =
  createLocalStorage("color-scheme");

const localBi$ = new Subject<ColorScheme | undefined>();
const local$ = localBi$.pipe(startWith(undefined));

function setColorSchemeInDOM(scheme: ColorScheme) {
  switch (scheme) {
    case "dark":
      document.documentElement.classList.add("dark");
      atomWriteAttribute(document.documentElement, "data-color-scheme", "dark");
      break;
    case "light":
    default:
      document.documentElement.classList.remove("dark");
      atomWriteAttribute(
        document.documentElement,
        "data-color-scheme",
        "light"
      );
      break;
  }
}

export function setColorScheme(scheme: ColorScheme): ColorScheme {
  setColorSchemeInLocalStorage(scheme);
  setColorSchemeInDOM(scheme);
  localBi$.next(scheme);
  return scheme;
}

// export function toggleColorScheme(): ColorScheme {
//   return setColorScheme(
//     getColorSchemeInLocalStorage() == "dark" ? "light" : "dark"
//   );
// }

export function getColorScheme$(): Observable<ColorScheme> {
  return combineLatest([
    local$,
    matchMedia$("(prefers-color-scheme: dark)")
  ]).pipe(
    map(([scheme, matchMediaDark]) => {
      if (scheme) {
        return scheme;
      }
      scheme = getColorSchemeInLocalStorage();
      if (!scheme) {
        // 如果用户没有初始设置过：获取「系统默认设置」
        scheme = matchMediaDark ? "dark" : "light";
        setColorSchemeInLocalStorage(scheme);
      }
      setColorSchemeInDOM(scheme);
      return scheme;
    })
  );
}

export function getRootCSSVar(name: string, defaultValue = ""): string {
  return (
    getComputedStyle(document.documentElement).getPropertyValue(name) ||
    defaultValue
  );
}

export function setRootCSSVar(name: string, value: string): void {
  // getComputedStyle(document.documentElement).setProperty(name, value);
  document.documentElement.style.setProperty(name, value);
}

export function getThemeColors(theme: Theme) {
  return {
    // 预定义颜色：深色模式下：颜色加深
    red: getRootCSSVar("--yz-red", "#fa503c"),
    redPanel: getRootCSSVar("--yz-red-panel", "#fff5f5"),
    redPanelBorder: getRootCSSVar("--yz-red-panel-border", "#fac8c8"),
    redGradient: getRootCSSVar("--yz-red-gradient", "#fa8c78"),
    green: getRootCSSVar("--yz-green", "#00be5a"),
    yellow: getRootCSSVar("--yz-yellow", "#fa8c00"),
    newenergy: getRootCSSVar("--yz-newenergy", "#64c896"),
    newenergyPanel: getRootCSSVar("--yz-newenergy-panel", "#ebfaf2"),

    success: getRootCSSVar("--yz-success", "#00be5a"),
    error: getRootCSSVar("--yz-error", "#fa503c"),
    warn: getRootCSSVar("--yz-warn", "#fa8c00"),

    // system：深色模式下：颜色加深
    foreground: getRootCSSVar(`--yz-foreground`, "#5a82f0"),
    foregroundSecondary: getRootCSSVar(`--yz-foreground-secondary`, "#e6f0ff"),
    foregroundGradient: getRootCSSVar(`--yz-foreground-gradient`, "#66b3ff"),
    background: getRootCSSVar(`--yz-background`, "#ffffff"),
    backgroundBorder: getRootCSSVar(`--yz-background-border`, "#e6e6e6"),
    panel: getRootCSSVar(`--yz-panel`, "#f5f5f5"),
    panelBorder: getRootCSSVar(`--yz-panel-border`, "#b3b3b3"),
    gap: getRootCSSVar(`--yz-gap`, "#cccccc"),
    placeholder: getRootCSSVar(`--yz-placeholder`, "#8f8f8f"),
    font: getRootCSSVar(`--yz-font`, "#333333"),
    fontSecondary: getRootCSSVar(`--yz-font-secondary`, "#666666"),
    fontTertiary: getRootCSSVar(`--yz-font-tertiary`, "#999999"),

    // system：深色模式下：颜色反向
    foregroundReverse: getRootCSSVar(`--yz-foreground-reverse`, "#5a82f0"),
    foregroundSecondaryReverse: getRootCSSVar(
      `--yz-foreground-secondary-reverse`,
      "#e6f0ff"
    ),
    foregroundGradientReverse: getRootCSSVar(
      `--yz-foreground-gradient-reverse`,
      "#66b3ff"
    ),
    backgroundReverse: getRootCSSVar(`--yz-background-reverse`, "#ffffff"),
    backgroundBorderReverse: getRootCSSVar(
      `--yz-background-border-reverse`,
      "#e6e6e6"
    ),
    panelReverse: getRootCSSVar(`--yz-panel-reverse`, "#f5f5f5"),
    panelBorderReverse: getRootCSSVar(`--yz-panel-border-reverse`, "#b3b3b3"),
    gapReverse: getRootCSSVar(`--yz-gap-reverse`, "#cccccc"),
    placeholderReverse: getRootCSSVar(`--yz-placeholder-reverse`, "#8f8f8f"),
    fontReverse: getRootCSSVar(`--yz-font-reverse`, "#333333"),
    fontSecondaryReverse: getRootCSSVar(
      `--yz-font-secondary-reverse`,
      "#666666"
    ),
    fontTertiaryReverse: getRootCSSVar(`--yz-font-tertiary-reverse`, "#999999"),

    // theme：深色模式下：颜色加深
    themePrimary: getRootCSSVar(`--yz-theme-${theme}-primary`, "#5a82f0"),
    themeSecondary: getRootCSSVar(`--yz-theme-${theme}-secondary`, "#e6f0ff"),
    themeGradient: getRootCSSVar(`--yz-theme-${theme}-gradient`, "#66b3ff"),
    themeBackground: getRootCSSVar(`--yz-theme-${theme}-background`, "#ffffff"),
    themeBackgroundBorder: getRootCSSVar(
      `--yz-theme-${theme}-background-border`,
      "#e6e6e6"
    ),
    themePanel: getRootCSSVar(`--yz-theme-${theme}-panel`, "#f5f5f5"),
    themePanelBorder: getRootCSSVar(
      `--yz-theme-${theme}-panel-border`,
      "#b3b3b3"
    ),
    themeGap: getRootCSSVar(`--yz-theme-${theme}-gap`, "#cccccc"),
    themePlaceholder: getRootCSSVar(
      `--yz-theme-${theme}-placeholder`,
      "#8f8f8f"
    ),
    themeFont: getRootCSSVar(`--yz-theme-${theme}-font`, "#333333"),
    themeFontSecondary: getRootCSSVar(
      `--yz-theme-${theme}-font-secondary`,
      "#666666"
    ),
    themeFontTertiary: getRootCSSVar(
      `--yz-theme-${theme}-font-tertiary`,
      "#999999"
    )
  };
}
